import type { InventoryRow } from '../types/inventoryRow';

export const POC_INVENTORY_NOM_EVENT = 'poc-inventory-nom-changed';

const STORAGE_KEY = 'poc-app-inventory-custom-noms-v1';

interface StorageShape {
  byReference: Record<string, string>;
}

const emptyShape = (): StorageShape => ({ byReference: {} });

const INV_FISCAL_26_PIERRE_REFERENCE = 'INV-2026-007';
const INV_FISCAL_26_PIERRE_NOM = 'Inventaire Fiscal 26 de Pierre';

function migrateLegacyInventoryNoms(data: StorageShape): StorageShape {
  const legacyNom = data.byReference[INV_FISCAL_26_PIERRE_REFERENCE];

  if (typeof legacyNom !== 'string' || legacyNom.trim().toLowerCase() !== 'rs') {
    return data;
  }

  const byReference = {
    ...data.byReference,
    [INV_FISCAL_26_PIERRE_REFERENCE]: INV_FISCAL_26_PIERRE_NOM,
  };

  writeStorage({ byReference });

  return { byReference };
}

function readStorage(): StorageShape {
  if (typeof window === 'undefined') {
    return emptyShape();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return emptyShape();
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== 'object') {
      return emptyShape();
    }

    const obj = parsed as { byReference?: unknown };

    if (!obj.byReference || typeof obj.byReference !== 'object') {
      return emptyShape();
    }

    return migrateLegacyInventoryNoms({
      byReference: obj.byReference as StorageShape['byReference'],
    });
  } catch {
    return emptyShape();
  }
}

function writeStorage(data: StorageShape): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(POC_INVENTORY_NOM_EVENT));
}

export function resolveInventoryNom(referenceInventaire: string, defaultNom: string): string {
  const stored = readStorage().byReference[referenceInventaire];

  if (typeof stored === 'string' && stored.trim() !== '') {
    return stored.trim();
  }

  const fallback = typeof defaultNom === 'string' ? defaultNom.trim() : '';

  return fallback !== '' ? fallback : referenceInventaire;
}

export function persistInventoryNom(referenceInventaire: string, nom: string): void {
  const trimmed = nom.trim();

  if (!trimmed) {
    return;
  }

  const data = readStorage();

  data.byReference[referenceInventaire] = trimmed;
  writeStorage(data);
}

export function applyInventoryNomToRow(row: InventoryRow): InventoryRow {
  return {
    ...row,
    nom: resolveInventoryNom(row.reference, row.nom),
  };
}
